import React from "react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DiscussIcon,
  StatusPill,
} from "../../../StrategyCanvas/StrategyCanvasHelpers";
import {
  COMMENT_SCOPE,
  OBJECTIVE_TYPE,
  REVIEW_STATUS,
  isNotSubmittedByMember,
} from "../../_config/quarterlyOkrConfig";
import {
  ContributorStack,
  LinkChip,
  PersonInline,
} from "../../_shared/QokrPrimitives/QokrPrimitives";
import QokrOkrDetails from "../../_shared/QokrOkrDetails/QokrOkrDetails";
import ReviewCommentThread from "../ReviewCommentThread/ReviewCommentThread";
import {
  commentKey,
  commentsFor,
  deletingCommentId,
} from "../useReviewActions";
import "../../_shared/_vendor/_company-okr-board.scss";
import "./_review-objective-row.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One objective in the coach's review (§9.3) — the Annual OKR review row
// (`rr-*` from `StrategyCanvasReviewMode.scss`, imported by the screen), so
// the Accept / To discuss controls are the same ones the coach uses on every
// other review. Marks are freely re-settable; there is no "clear".
//
// The flags the contract asks to surface sit on the header so a coach sees
// them collapsed: not submitted by the member (early send), kept AI draft the
// member never accepted, and a deactivated owner.
// ═══════════════════════════════════════════════════════════════════════════

const flagsFor = (type, objective, member) => {
  const flags = [];
  if (type === OBJECTIVE_TYPE.INDIVIDUAL) {
    if (isNotSubmittedByMember(member?.submission_kind)) {
      flags.push("Not submitted by the member");
    } else if (objective.accepted_by_member === false) {
      flags.push("Not accepted by the member");
    }
  }
  if (objective.owner?.is_deactivated || objective.member?.is_deactivated) {
    flags.push("Owner deactivated");
  }
  return flags;
};

const ReviewObjectiveRow = ({
  type,
  objective,
  member = null,
  linkLabel = null,
  comments,
  actions,
  expanded,
  onToggle,
  readOnly,
  flagged = false,
}) => {
  const status = objective.review?.status || null;
  const target = {
    scope: COMMENT_SCOPE.OBJECTIVE,
    type,
    objectiveId: objective.objective_id,
  };
  // No "marking" lock on Accept / To discuss (2026-10-21, by instruction):
  // `setReviewStatus` is optimistic — the pill moves on click, the PUT runs in
  // the background and a failure restores the previous status.
  const owner =
    type === OBJECTIVE_TYPE.COMPANY ? objective.owner : objective.member;
  const flags = flagsFor(type, objective, member);

  return (
    <div
      className={[
        "rr-row qokr-review-row",
        status ? `rr-${status}` : "",
        flagged ? "rr-row--pending" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-review-target={`${type}-${objective.objective_id}`}
    >
      <button type="button" className="rr-row-header" onClick={onToggle}>
        <span className="rr-row-left">
          <span className="rr-row-label">
            {objective.objective || "Untitled objective"}
          </span>
        </span>
        <span className="rr-row-right">
          {flags.map((flag) => (
            <span key={flag} className="qokr-pill qokr-pill--amber">
              {flag}
            </span>
          ))}
          {status ? (
            <StatusPill
              accepted={status === REVIEW_STATUS.ACCEPTED}
              className="rr-status-pill"
              acceptedClassName="rr-status-accepted"
              discussClassName="rr-status-to_discuss"
            />
          ) : null}
          <span className="rr-chevron">
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </span>
      </button>

      {expanded ? (
        <div className="rr-row-body">
          <div className="cob__row-meta qokr-review-row__meta">
            <PersonInline person={owner} />
            <ContributorStack people={objective.contributors || []} />
            {type === OBJECTIVE_TYPE.COMPANY ? (
              <LinkChip label={objective.annual_okr} kind="company" />
            ) : (
              <LinkChip
                label={linkLabel}
                kind="individual"
                relationship={objective.relationship}
              />
            )}
          </div>

          <QokrOkrDetails
            keyResults={objective.key_results || []}
            initiatives={objective.initiatives || []}
          />

          {!readOnly ? (
            <div className="rr-actions">
              <button
                type="button"
                className={`rr-action-btn rr-accept ${
                  status === REVIEW_STATUS.ACCEPTED ? "active" : ""
                }`}
                onClick={() =>
                  actions.mark(type, objective, REVIEW_STATUS.ACCEPTED)
                }
              >
                <CheckIcon size={12} />
                Accept
              </button>
              <button
                type="button"
                className={`rr-action-btn rr-discuss ${
                  status === REVIEW_STATUS.TO_DISCUSS ? "active" : ""
                }`}
                onClick={() =>
                  actions.mark(type, objective, REVIEW_STATUS.TO_DISCUSS)
                }
              >
                <DiscussIcon size={12} />
                To discuss in the live review
              </button>
            </div>
          ) : null}

          <ReviewCommentThread
            comments={commentsFor(comments, target)}
            readOnly={readOnly}
            saving={Boolean(actions.pending[commentKey(target)])}
            deletingId={deletingCommentId(actions.pending)}
            onAdd={(text) => actions.addComment(target, text)}
            onDelete={actions.deleteComment}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ReviewObjectiveRow;
